import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import axios from "axios";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import { scrollTo } from "../components/landing/Header";
import { API } from "../lib/api";
import { featureHref, getFeaturePage, mergeFeaturePage, resolveFeatureSlug } from "../lib/featurePages";

const mediaUrl = (p) => {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${API}/media/${p}`;
};

export default function FeaturePage() {
  const { slug: rawSlug } = useParams();
  const slug = resolveFeatureSlug(rawSlug);
  const fallback = getFeaturePage(slug);
  const [page, setPage] = useState(fallback);

  useEffect(() => {
    const local = getFeaturePage(slug);
    setPage(local);
    window.scrollTo(0, 0);
    if (local?.title) document.title = `${local.title} — simplyOwner`;
    if (!slug) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/pages/${slug}`)
      .then((r) => {
        if (!cancelled) setPage(mergeFeaturePage(local, r.data));
      })
      .catch(() => {
        if (!cancelled) setPage(local);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const missing = !page;

  return (
    <main data-testid="feature-page" className="relative">
      <Header />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-16">
        {missing && (
          <div className="py-24 text-center">
            <h1 className="font-display font-black text-4xl text-ink">Page not found</h1>
            <Link to="/features" className="mt-6 inline-block font-mono text-xs tracking-[0.2em] uppercase text-brand">
              All features
            </Link>
          </div>
        )}
        {page && (
          <>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
            >
              {page.group || "Product"}
            </motion.p>
            <motion.h1
              data-testid="feature-title"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl"
            >
              {page.title}
            </motion.h1>
            <motion.p
              data-testid="feature-tagline"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-ink/65 text-lg font-light max-w-2xl leading-relaxed"
            >
              {page.tagline}
            </motion.p>

            {page.bullets?.length > 0 && (
              <motion.div
                data-testid="feature-bullets"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl"
              >
                {page.bullets.map((b) => (
                  <div key={b} className="flex items-start gap-3 rounded-xl border border-black/10 bg-white px-5 py-4">
                    <Check className="w-4 h-4 text-brand mt-1 shrink-0" />
                    <span className="text-[15px] text-ink/80 font-light">{b}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </>
        )}
      </section>

      {page && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 space-y-20">
          {page.sections?.map((s, i) => (
            <motion.div
              key={`${s.heading}-${i}`}
              data-testid={`feature-section-${i + 1}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 ${s.image ? "lg:grid-cols-2" : ""} gap-12 items-center`}
            >
              <div className={s.image && i % 2 === 1 ? "lg:order-2" : ""}>
                <h2 className="font-display font-black uppercase tracking-tight text-2xl lg:text-4xl text-ink">{s.heading}</h2>
                <p className="mt-5 text-ink/65 font-light leading-relaxed text-base lg:text-lg whitespace-pre-line">{s.text}</p>
              </div>
              {s.image && (
                <div className={`rounded-2xl overflow-hidden border border-black/10 card-glow ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <img src={mediaUrl(s.image)} alt={s.heading} className="w-full aspect-[4/3] object-cover spotlight-img" />
                </div>
              )}
            </motion.div>
          ))}

          {page.related?.length > 0 && (
            <div>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6">Related</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {page.related.map((rel) => {
                  const item = getFeaturePage(rel);
                  if (!item) return null;
                  return (
                    <Link
                      key={rel}
                      to={featureHref(rel)}
                      data-testid={`feature-related-${rel}`}
                      className="rounded-xl border border-black/10 bg-white px-6 py-5 hover:border-brand/60 transition-colors duration-300"
                    >
                      <div className="font-display font-bold text-ink">{item.title}</div>
                      <p className="mt-2 text-sm text-ink/55 font-light leading-snug">{item.tagline}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl bg-black text-white p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8"
          >
            <div>
              <h2 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl">One price. Zero commission.</h2>
              <p className="mt-3 text-white/60 font-light max-w-xl">This feature — and everything else — is included in your one-time package. Live in under 7 days.</p>
            </div>
            <button
              data-testid="feature-cta"
              onClick={() => scrollTo("/#contact")}
              className="group shrink-0 flex items-center gap-3 bg-brand hover:bg-white hover:text-black text-white font-display font-bold uppercase tracking-wide px-9 py-4 rounded-full transition-colors duration-300"
            >
              Become the owner
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </motion.div>
        </section>
      )}
      <Footer />
    </main>
  );
}
